import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../services/supabaseClient';
import { deriveBillingState } from '../utils/orgBilling';

const SubscriptionContext = createContext({});

// Feature keys — every controllable page/feature in the app
export const FEATURE_KEYS = {
  DASHBOARD: 'dashboard',
  CALENDAR: 'calendar',
  DELIVERY_STATUS: 'delivery_status',
  MEALS: 'meals',
  HELP: 'help',
  COMMUNITY: 'community',
  INGREDIENTS: 'ingredients',
  ANALYTICS: 'analytics',
  AUDIT_LOGS: 'audit_logs',
  COMPLIANCE: 'compliance',
  ADMIN_USERS: 'admin_users',
  ADMIN_CARE_HOMES: 'admin_care_homes',
  ADMIN_RESIDENTS: 'admin_residents',
  RESIDENTS: 'residents',
  STAFF_MEALS: 'staff_meals',
  EMAIL_DIGEST: 'email_digest',
};

// Activity Planner feature keys stored in organizations.features
export const ACTIVITY_FEATURE_KEYS = {
  DASHBOARD: 'activity_dashboard',
  HELP: 'activity_help',
  CALENDAR: 'activity_calendar',
  ACTIVITIES: 'activity_activities',
  COMMUNITY: 'activity_community',
  SESSIONS: 'activity_sessions',
  ANALYTICS: 'activity_analytics',
  ENROLLMENTS: 'activity_enrollments',
  EXPENSES: 'activity_expenses',
  AUDIT_LOGS: 'activity_audit_logs',
  GALLERY: 'activity_gallery',
  ADMIN_USERS: 'activity_admin_users',
  ADMIN_CARE_HOMES: 'activity_admin_care_homes',
  ADMIN_RESIDENTS: 'activity_admin_residents',
  STAFF_DASHBOARD: 'activity_staff_dashboard',
  STAFF_RESIDENTS: 'activity_staff_residents',
  TV_DISPLAY_CONTROL: 'activity_tv_display_control',
};

// Activity Planner features included in the Free tier
export const ACTIVITY_DEFAULT_FREE_FEATURES = [
  ACTIVITY_FEATURE_KEYS.DASHBOARD,
  ACTIVITY_FEATURE_KEYS.HELP,
  ACTIVITY_FEATURE_KEYS.CALENDAR,
  ACTIVITY_FEATURE_KEYS.ACTIVITIES,
  ACTIVITY_FEATURE_KEYS.COMMUNITY,
  ACTIVITY_FEATURE_KEYS.SESSIONS,
  ACTIVITY_FEATURE_KEYS.ENROLLMENTS,
  ACTIVITY_FEATURE_KEYS.ADMIN_RESIDENTS,
];

// Cross-app access flags stored in organizations.features
export const APP_ACCESS_KEYS = {
  MEAL_MANAGER: 'app_meal_manager',
  ACTIVITY_PLANNER: 'app_activity_planner',
};

// Default free-tier features (used when no DB features set)
export const DEFAULT_FREE_FEATURES = [
  FEATURE_KEYS.DASHBOARD,
  FEATURE_KEYS.CALENDAR,
  FEATURE_KEYS.DELIVERY_STATUS,
  FEATURE_KEYS.MEALS,
  FEATURE_KEYS.HELP,
  FEATURE_KEYS.COMMUNITY,
];

// Plan templates — Free and Enterprise only
// Each array lists the feature keys that should be ENABLED for that plan.
export const PLAN_TEMPLATES = {
  free: [...DEFAULT_FREE_FEATURES],
  enterprise: Object.values(FEATURE_KEYS), // all Meal Manager features
};

// Activity Planner plan templates (applied alongside Meal Manager plan when using combined panel)
export const ACTIVITY_PLAN_TEMPLATES = {
  free: [...ACTIVITY_DEFAULT_FREE_FEATURES],
  enterprise: Object.values(ACTIVITY_FEATURE_KEYS),
};

// Human-readable labels for each feature
export const FEATURE_LABELS = {
  [FEATURE_KEYS.DASHBOARD]: 'Dashboard',
  [FEATURE_KEYS.CALENDAR]: 'Food Calendar',
  [FEATURE_KEYS.DELIVERY_STATUS]: 'Delivery Status',
  [FEATURE_KEYS.MEALS]: 'Meals Management',
  [FEATURE_KEYS.HELP]: 'Help & Support',
  [FEATURE_KEYS.COMMUNITY]: 'Community',
  [FEATURE_KEYS.INGREDIENTS]: 'Ingredients Management',
  [FEATURE_KEYS.ANALYTICS]: 'Cost Analytics',
  [FEATURE_KEYS.AUDIT_LOGS]: 'Audit Logs',
  [FEATURE_KEYS.COMPLIANCE]: 'Compliance Reporting',
  [FEATURE_KEYS.ADMIN_USERS]: 'User Management',
  [FEATURE_KEYS.ADMIN_CARE_HOMES]: 'Care Home Management',
  [FEATURE_KEYS.ADMIN_RESIDENTS]: 'Resident Management',
  [FEATURE_KEYS.RESIDENTS]: 'Staff Residents',
  [FEATURE_KEYS.STAFF_MEALS]: 'Staff Meals',
  [FEATURE_KEYS.EMAIL_DIGEST]: 'Email Digest',
};
export const FEATURE_ICONS = {
  [FEATURE_KEYS.DASHBOARD]: 'LayoutDashboard',
  [FEATURE_KEYS.CALENDAR]: 'Calendar',
  [FEATURE_KEYS.DELIVERY_STATUS]: 'ClipboardCheck',
  [FEATURE_KEYS.MEALS]: 'UtensilsCrossed',
  [FEATURE_KEYS.HELP]: 'HelpCircle',
  [FEATURE_KEYS.COMMUNITY]: 'MessagesSquare',
  [FEATURE_KEYS.INGREDIENTS]: 'Leaf',
  [FEATURE_KEYS.ANALYTICS]: 'TrendingUp',
  [FEATURE_KEYS.AUDIT_LOGS]: 'FileText',
  [FEATURE_KEYS.COMPLIANCE]: 'FileCheck',
  [FEATURE_KEYS.ADMIN_USERS]: 'Users',
  [FEATURE_KEYS.ADMIN_CARE_HOMES]: 'Building2',
  [FEATURE_KEYS.ADMIN_RESIDENTS]: 'UserCheck',
  [FEATURE_KEYS.RESIDENTS]: 'Users',
  [FEATURE_KEYS.STAFF_MEALS]: 'ChefHat',
  [FEATURE_KEYS.EMAIL_DIGEST]: 'Mail',
};

export const ACTIVITY_FEATURE_LABELS = {
  [ACTIVITY_FEATURE_KEYS.DASHBOARD]: 'Dashboard',
  [ACTIVITY_FEATURE_KEYS.HELP]: 'Help & Documentation',
  [ACTIVITY_FEATURE_KEYS.CALENDAR]: 'Activity Calendar',
  [ACTIVITY_FEATURE_KEYS.ACTIVITIES]: 'Activities Library',
  [ACTIVITY_FEATURE_KEYS.COMMUNITY]: 'Community',
  [ACTIVITY_FEATURE_KEYS.SESSIONS]: 'Sessions',
  [ACTIVITY_FEATURE_KEYS.ANALYTICS]: 'Analytics',
  [ACTIVITY_FEATURE_KEYS.ENROLLMENTS]: 'Enrollments & Residents',
  [ACTIVITY_FEATURE_KEYS.EXPENSES]: 'Expenses',
  [ACTIVITY_FEATURE_KEYS.AUDIT_LOGS]: 'Audit Logs',
  [ACTIVITY_FEATURE_KEYS.GALLERY]: 'Photo Gallery',
  [ACTIVITY_FEATURE_KEYS.ADMIN_USERS]: 'User Management',
  [ACTIVITY_FEATURE_KEYS.ADMIN_CARE_HOMES]: 'Care Home Management',
  [ACTIVITY_FEATURE_KEYS.ADMIN_RESIDENTS]: 'Resident Management',
  [ACTIVITY_FEATURE_KEYS.STAFF_DASHBOARD]: 'Staff Dashboard',
  [ACTIVITY_FEATURE_KEYS.STAFF_RESIDENTS]: 'Staff Residents',
  [ACTIVITY_FEATURE_KEYS.TV_DISPLAY_CONTROL]: 'TV Display Control',
};

export const ACTIVITY_FEATURE_ICONS = {
  [ACTIVITY_FEATURE_KEYS.DASHBOARD]: 'LayoutDashboard',
  [ACTIVITY_FEATURE_KEYS.HELP]: 'HelpCircle',
  [ACTIVITY_FEATURE_KEYS.CALENDAR]: 'Calendar',
  [ACTIVITY_FEATURE_KEYS.ACTIVITIES]: 'Activity',
  [ACTIVITY_FEATURE_KEYS.COMMUNITY]: 'MessagesSquare',
  [ACTIVITY_FEATURE_KEYS.SESSIONS]: 'Clock3',
  [ACTIVITY_FEATURE_KEYS.ANALYTICS]: 'BarChart3',
  [ACTIVITY_FEATURE_KEYS.ENROLLMENTS]: 'Users',
  [ACTIVITY_FEATURE_KEYS.EXPENSES]: 'PoundSterling',
  [ACTIVITY_FEATURE_KEYS.AUDIT_LOGS]: 'FileText',
  [ACTIVITY_FEATURE_KEYS.GALLERY]: 'Images',
  [ACTIVITY_FEATURE_KEYS.ADMIN_USERS]: 'UserCog',
  [ACTIVITY_FEATURE_KEYS.ADMIN_CARE_HOMES]: 'Building2',
  [ACTIVITY_FEATURE_KEYS.ADMIN_RESIDENTS]: 'UserCheck',
  [ACTIVITY_FEATURE_KEYS.STAFF_DASHBOARD]: 'LayoutDashboard',
  [ACTIVITY_FEATURE_KEYS.STAFF_RESIDENTS]: 'Users',
  [ACTIVITY_FEATURE_KEYS.TV_DISPLAY_CONTROL]: 'Monitor',
};

// Map route paths to feature keys
export const ROUTE_FEATURE_MAP = {
  '/main-dashboard': FEATURE_KEYS.DASHBOARD,
  '/food-calendar-view': FEATURE_KEYS.CALENDAR,
  '/delivery-status': FEATURE_KEYS.DELIVERY_STATUS,
  '/meals-management': FEATURE_KEYS.MEALS,
  '/help': FEATURE_KEYS.HELP,
  '/community': FEATURE_KEYS.COMMUNITY,
  '/ingredients-management': FEATURE_KEYS.INGREDIENTS,
  '/cost-analytics': FEATURE_KEYS.ANALYTICS,
  '/cost-analytics/breakdown': FEATURE_KEYS.ANALYTICS,
  '/audit-logs': FEATURE_KEYS.AUDIT_LOGS,
  '/compliance-reporting': FEATURE_KEYS.COMPLIANCE,
  '/admin/user-management': FEATURE_KEYS.ADMIN_USERS,
  '/admin/care-home-management': FEATURE_KEYS.ADMIN_CARE_HOMES,
  '/admin/resident-management': FEATURE_KEYS.ADMIN_RESIDENTS,
  '/staff/residents': FEATURE_KEYS.RESIDENTS,
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, organizationId, isSuperAdmin } = useAuth();
  const userId = user?.id || null;
  const [subscription, setSubscription] = useState(null);
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [appAccess, setAppAccess] = useState({ mealManager: true, activityPlanner: true });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load organization subscription data
  const loadSubscription = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    if (!userId) {
      setSubscription(null);
      setEnabledFeatures({});
      setAppAccess({ mealManager: true, activityPlanner: true });
      setIsLoading(false);
      return;
    }

    // Super Admins always get all features
    if (isSuperAdmin) {
      const allFeatures = {};
      Object.values(FEATURE_KEYS).forEach(k => { allFeatures[k] = true; });
      setEnabledFeatures(allFeatures);
      setAppAccess({ mealManager: true, activityPlanner: true });
      setSubscription({ plan: 'enterprise', status: 'active' });
      setIsLoading(false);
      return;
    }

    if (!organizationId) {
      // No organization — give default free features only
      const freeOnly = {};
      DEFAULT_FREE_FEATURES.forEach(k => { freeOnly[k] = true; });
      setEnabledFeatures(freeOnly);
      setAppAccess({ mealManager: true, activityPlanner: true });
      setSubscription({ plan: 'free', status: 'active' });
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select('subscription_plan, subscription_status, features, trial_ends_at, max_care_homes, max_users, name, billing_grace_ends_at, billing_locked_at, stripe_subscription_status, billing_unit_amount_pence, billing_home_count_override, billed_care_home_count, billing_access_override, is_active')
        .eq('id', organizationId)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        const plan = data.subscription_plan || 'free';
        const status = data.subscription_status || 'trial';
        const billingOrg = {
          trial_ends_at: data.trial_ends_at,
          billing_grace_ends_at: data.billing_grace_ends_at,
          billing_locked_at: data.billing_locked_at,
          stripe_subscription_status: data.stripe_subscription_status,
          billing_access_override: data.billing_access_override,
          is_active: data.is_active,
        };
        const billing = deriveBillingState(billingOrg);
        const effectiveStatus = billing.phase === 'locked' ? 'expired' : status;

        setSubscription({
          plan,
          status: effectiveStatus,
          trialEndsAt: data.trial_ends_at,
          maxCareHomes: data.max_care_homes,
          maxUsers: data.max_users,
          orgName: data.name,
          billing_grace_ends_at: data.billing_grace_ends_at,
          billing_locked_at: data.billing_locked_at,
          stripe_subscription_status: data.stripe_subscription_status,
          billing_unit_amount_pence: data.billing_unit_amount_pence,
          billing_home_count_override: data.billing_home_count_override,
          billed_care_home_count: data.billed_care_home_count,
          billing_access_override: data.billing_access_override,
          is_active: data.is_active,
        });

        // Build enabled features map from DB — this is the source of truth set by Super Admin
        const featuresMap = {};
        const dbFeatures = data.features || {};
        const nextAppAccess = {
          mealManager: dbFeatures[APP_ACCESS_KEYS.MEAL_MANAGER] !== false,
          activityPlanner: dbFeatures[APP_ACCESS_KEYS.ACTIVITY_PLANNER] !== false,
        };

        // If suspended/cancelled/expired, only default free features
        if (effectiveStatus === 'suspended' || effectiveStatus === 'cancelled' || effectiveStatus === 'expired') {
          DEFAULT_FREE_FEATURES.forEach(k => { featuresMap[k] = true; });
        } else {
          // Every feature is controlled by the DB value — Super Admin has full control
          Object.values(FEATURE_KEYS).forEach(key => {
            featuresMap[key] = dbFeatures[key] === true;
          });
        }

        setEnabledFeatures(featuresMap);
        setAppAccess(nextAppAccess);
      } else {
        // Organization not found — free only
        const freeOnly = {};
        DEFAULT_FREE_FEATURES.forEach(k => { freeOnly[k] = true; });
        setEnabledFeatures(freeOnly);
        setAppAccess({ mealManager: true, activityPlanner: true });
        setSubscription({ plan: 'free', status: 'active' });
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError(err.message);
      // On error, default to free features only so the app doesn't break
      const freeOnly = {};
      DEFAULT_FREE_FEATURES.forEach(k => { freeOnly[k] = true; });
      setEnabledFeatures(freeOnly);
      setAppAccess({ mealManager: true, activityPlanner: true });
      setSubscription({ plan: 'free', status: 'active' });
    } finally {
      setIsLoading(false);
    }
  }, [userId, organizationId, isSuperAdmin]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Check if a specific feature is enabled
  const hasFeature = useCallback((featureKey) => {
    if (isSuperAdmin) return true;
    return enabledFeatures[featureKey] === true;
  }, [enabledFeatures, isSuperAdmin]);

  // Check if a route path is accessible
  const canAccessRoute = useCallback((path) => {
    if (isSuperAdmin) return true;
    const featureKey = ROUTE_FEATURE_MAP[path];
    if (!featureKey) return true; // Unknown routes are allowed
    return hasFeature(featureKey);
  }, [hasFeature, isSuperAdmin]);

  const value = useMemo(() => ({
    subscription,
    enabledFeatures,
    appAccess,
    isLoading,
    error,
    hasFeature,
    canAccessRoute,
    reload: loadSubscription,
    billingState: isSuperAdmin ? { phase: 'none', blocking: false, remind: false } : deriveBillingState({
      trial_ends_at: subscription?.trialEndsAt,
      billing_grace_ends_at: subscription?.billing_grace_ends_at,
      billing_locked_at: subscription?.billing_locked_at,
      stripe_subscription_status: subscription?.stripe_subscription_status,
      billing_access_override: subscription?.billing_access_override,
      is_active: subscription?.is_active,
    }),
    isFree: subscription?.plan === 'free',
    isPaid: subscription?.plan === 'enterprise',
    planName: subscription?.plan || 'free',
  }), [subscription, enabledFeatures, appAccess, isLoading, error, hasFeature, canAccessRoute, loadSubscription]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export default SubscriptionContext;
